// /link/return?return_to=…#account_code=… — the cloud return page for
// boat-side "Sign in with Google" (task cloud_account_link_code, T-ACCT-02).
//
// After the Google OIDC callback mints a one-time account-link code it
// redirects the user's browser here with the code in the URL FRAGMENT (never
// the query, so no server ever logs it). This page is DISPLAY-ONLY: it renders
// a single "Return to your boat" link to <return_to>/#account_code=<code>. The
// cloud never redirects to the boat itself (open-redirect prevention) — the
// user taps the link, the boat status page reloads with #account_code, and the
// boat redeems it for a session. Mirrors the Activate "Return to your boat"
// pattern. Unauthenticated on the cloud side (the session lives on the cloud
// domain; the boat gets its own via the code), so this route is deliberately
// placed under MarketingLayout, NOT RequireAuth.
//
// Params arrive via the full-page OIDC 302 (router state can't survive it):
// `return_to` (not secret) rides in the query, but the one-time `account_code`
// rides in the URL FRAGMENT (`#account_code=…`) so it is never sent to — or
// logged by — any server (T-ACCT-02). We read it from location.hash and hand it
// to the boat the same way (in the boat link's fragment), so the boat's own
// server never logs it either.

import { useSearchParams } from 'react-router-dom';

import { useDocumentTitle } from '../lib/useDocumentTitle';
import { safeReturnTo } from '../lib/returnTo';

function accountCodeFromHash(): string {
  // location.hash is "#account_code=…"; never sent to the server.
  const hash = typeof window === 'undefined' ? '' : window.location.hash;
  return (new URLSearchParams(hash.replace(/^#/, '')).get('account_code') ?? '').trim();
}

export function LinkReturn() {
  useDocumentTitle('Return to your boat - Neomaris');
  const [searchParams] = useSearchParams();
  const accountCode = accountCodeFromHash();
  // The boat's own LAN origin, guarded to a valid http(s) URL (display-only
  // link, never navigated to programmatically — mirrors validateReturnTo).
  const boatHref = (() => {
    const rt = safeReturnTo(searchParams.get('return_to'));
    if (rt === null || accountCode.length === 0) return null;
    try {
      const u = new URL(rt);
      // Carry the code in the boat URL's FRAGMENT so the boat server never logs
      // it; the status page reads it from location.hash.
      u.hash = new URLSearchParams({ account_code: accountCode }).toString();
      return u.toString();
    } catch {
      return null;
    }
  })();

  if (boatHref === null) {
    return (
      <section className="auth-panel">
        <div className="auth-card">
          <h2>This sign-in link is incomplete</h2>
          <ul className="form-errors" data-testid="error-banner">
            <li>
              We couldn’t finish linking your account. Start “Sign in with Google” again from your
              boat’s page.
            </li>
          </ul>
        </div>
      </section>
    );
  }

  return (
    <section className="auth-panel">
      <div className="auth-card" data-testid="link-return-success">
        <h2>You’re signed in</h2>
        <p className="auth-sub">
          Head back to your boat to finish — it picks up right where you left off and shows your
          boats.
        </p>
        {/* Display-only return link to the boat's LAN origin, carrying the
            one-time account-link code the boat redeems for its session. */}
        <a href={boatHref} className="btn btn-primary auth-submit" data-testid="return-to-boat">
          Return to your boat
        </a>
      </div>
    </section>
  );
}
